import { useEffect } from 'react';
import { useProjectStore } from '../../state/projectStore';
import { spreadCount } from './canvas/scene';

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
}

/**
 * Global editor keyboard shortcuts. See `editor.shortcuts` in the i18n
 * dictionary for the user-facing documentation of these bindings.
 */
export function useEditorShortcuts() {
  const undo = useProjectStore((s) => s.undo);
  const redo = useProjectStore((s) => s.redo);
  const saveNow = useProjectStore((s) => s.saveNow);
  const removeElement = useProjectStore((s) => s.removeElement);
  const duplicateElement = useProjectStore((s) => s.duplicateElement);
  const selection = useProjectStore((s) => s.selection);
  const cropElementId = useProjectStore((s) => s.cropElementId);
  const exitCropMode = useProjectStore((s) => s.exitCropMode);
  const clearSelection = useProjectStore((s) => s.clearSelection);
  const setZoom = useProjectStore((s) => s.setZoom);
  const zoom = useProjectStore((s) => s.zoom);
  const view = useProjectStore((s) => s.view);
  const setView = useProjectStore((s) => s.setView);
  const project = useProjectStore((s) => s.project);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (isEditableTarget(e.target)) return;
      const mod = e.metaKey || e.ctrlKey;

      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        redo();
        return;
      }
      if (mod && e.key.toLowerCase() === 's') {
        e.preventDefault();
        void saveNow();
        return;
      }
      if (mod && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        if (selection.target && selection.elementIds[0]) {
          duplicateElement(selection.target, selection.elementIds[0]);
        }
        return;
      }
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        selection.target &&
        selection.elementIds.length > 0
      ) {
        e.preventDefault();
        for (const id of selection.elementIds) removeElement(selection.target, id);
        return;
      }
      if (e.key === 'Escape') {
        if (cropElementId) exitCropMode();
        else clearSelection();
        return;
      }
      if (e.key === 'Enter' && cropElementId) {
        exitCropMode();
        return;
      }
      if ((e.key === '+' || e.key === '=') && mod) {
        e.preventDefault();
        setZoom(zoom + 0.1);
        return;
      }
      if (e.key === '-' && mod) {
        e.preventDefault();
        setZoom(zoom - 0.1);
        return;
      }
      if (view.mode === 'spread' && project) {
        const total = spreadCount(project);
        if (e.key === ']' || e.key === 'PageDown') {
          e.preventDefault();
          setView({ mode: 'spread', spreadIndex: Math.min(total - 1, view.spreadIndex + 1) });
          return;
        }
        if (e.key === '[' || e.key === 'PageUp') {
          e.preventDefault();
          setView({ mode: 'spread', spreadIndex: Math.max(0, view.spreadIndex - 1) });
          return;
        }
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    undo,
    redo,
    saveNow,
    removeElement,
    duplicateElement,
    selection,
    cropElementId,
    exitCropMode,
    clearSelection,
    setZoom,
    zoom,
    view,
    setView,
    project,
  ]);
}
